import React, { useState, useRef } from 'react';
import DOMPurify from 'dompurify';
import { Grid, Box, Typography, Tooltip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Chip } from '@mui/material';
import NoteIcon from '@mui/icons-material/Note';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import TranslateIcon from '@mui/icons-material/Translate';
import AddCommentIcon from '@mui/icons-material/AddComment';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SpellcheckEditor from './SpellcheckEditor';
import TagTextField from './TagTextField';
import './TranslationRow.css';

const TranslationRow = ({
    globalIndex,
    originalText,
    translatedText,
    isRepeat,
    tagMap,
    isProjectOwner,
    sourceDirection,
    targetDirection,
    grammarErrors,
    hasNote,
    isApproved,
    confidenceScore,
    translationSource,
    onOriginalTextChange,
    onTranslationChange,
    onGrammarReplace, // Used by SpellcheckEditor
    onGrammarClick,   // Used by non-editable view
    highlightGrammarIssues, // Helper function passed down
    onToggleApproval,
    onTranslate,
    onAddAnnotation,
    onCopyToClipboard,
    onAddGlossaryTerm,
    onRowFocus
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const rowRef = useRef(null);

    const handleOpenMore = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMore = () => {
        setAnchorEl(null);
    };

    return (
        <Grid item xs={12}>
            <Box
                ref={rowRef}
                className="segment-row"
                sx={{ position: 'relative', py: 1 }}
                onFocusCapture={() => onRowFocus && onRowFocus(globalIndex)}
            >
                <Grid container spacing={2} alignItems="flex-start">
                    {/* Segment Number + RP + Page */}
                    <Grid item sx={{ width: 60, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        {tagMap && tagMap._pageContext && (
                            <Tooltip title={`Page ${tagMap._pageContext}`}>
                                <Chip
                                    label={`P${tagMap._pageContext}`}
                                    size="small"
                                    sx={{
                                        height: 16,
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        mb: 0.5,
                                        backgroundColor: '#f5f5f5',
                                        color: '#666',
                                        border: '1px solid #ddd'
                                    }}
                                />
                            </Tooltip>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {isRepeat && (
                                <Tooltip title="Repeated Section">
                                    <Box component="span" sx={{
                                        background: '#e1e7f5',
                                        color: '#234fd2',
                                        borderRadius: 1,
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        px: 1, mr: 1,
                                        border: '1px solid #bfc9e8'
                                    }}>
                                        RP
                                    </Box>
                                </Tooltip>
                            )}
                            <Typography variant="body2">{globalIndex + 1}</Typography>
                        </Box>
                        {hasNote && (
                            <Tooltip title="This section has a note">
                                <IconButton size="small" color="secondary" sx={{ position: 'absolute', bottom: -10, right: -15 }}>
                                    <NoteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Grid>

                    {/* Original Text */}
                    <Grid item xs>
                        {isProjectOwner ? (
                            <SpellcheckEditor
                                dir={sourceDirection}
                                tagMap={tagMap}
                                style={{
                                    minHeight: 56,
                                    border: '1px solid rgba(0,0,0,0.23)',
                                    borderRadius: 4,
                                    padding: '16.5px 14px',
                                    boxSizing: 'border-box',
                                    textAlign: sourceDirection === 'rtl' ? 'right' : 'left'
                                }}
                                value={originalText}
                                onChange={newText => onOriginalTextChange(globalIndex, newText)}
                                errors={grammarErrors || []}
                                onReplace={(sug, errIdx) => onGrammarReplace && onGrammarReplace(globalIndex, sug, errIdx)}
                                readOnly={false}
                            />
                        ) : (
                            <Box
                                sx={{ border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1, p: '16.5px 14px', minHeight: 56, whiteSpace: 'pre-wrap', textAlign: sourceDirection === 'rtl' ? 'right' : 'left' }}
                                dir={sourceDirection}
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(highlightGrammarIssues ? highlightGrammarIssues(globalIndex, originalText, grammarErrors || []) : originalText) }}
                                onClick={onGrammarClick}
                            />
                        )}
                    </Grid>

                    {/* Translated Text */}
                    <Grid item xs>
                        <TagTextField
                            value={translatedText || ''}
                            tagMap={tagMap}
                            onChange={v => onTranslationChange(globalIndex, v)}
                            dir={targetDirection}
                            style={{
                                textAlign: targetDirection === 'rtl' ? 'right' : 'left',
                                backgroundColor: isApproved ? '#f0f0f0' : 'white',
                                minHeight: 56,
                                border: '1px solid rgba(0,0,0,0.23)',
                                borderRadius: 4,
                                padding: '16.5px 14px',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}
                            disabled={isApproved}
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                            {translationSource
                                ? `${translationSource === 'memory' ? 'Memory' : 'AI'} (${Math.round(confidenceScore || 0)}%)`
                                : `Confidence: ${Math.round(confidenceScore || 0)}%`}
                        </Typography>
                    </Grid>

                    {/* Expanded Actions Column */}
                    <Grid item sx={{ width: 150, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Box className="segment-toolbar">
                            <Tooltip title={isApproved ? 'Unapprove' : 'Approve'}>
                                <IconButton
                                    size="small"
                                    className={`toolbar-action ${isApproved ? 'unapprove-btn' : 'approve-btn'}`}
                                    onClick={() => onToggleApproval(globalIndex)}
                                >
                                    {isApproved ? <UndoIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>

                            {isProjectOwner && (
                                <Tooltip title="Translate">
                                    <IconButton
                                        size="small"
                                        className="toolbar-action translate-btn"
                                        onClick={() => onTranslate(globalIndex)}
                                        disabled={isApproved}
                                    >
                                        <TranslateIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}

                            <Tooltip title="Add Annotation">
                                <IconButton
                                    size="small"
                                    className="toolbar-action"
                                    onClick={() => onAddAnnotation(globalIndex)}
                                >
                                    <AddCommentIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>

                            <div className="toolbar-divider" />

                            <IconButton
                                size="small"
                                className="toolbar-action"
                                onClick={handleOpenMore}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Grid>

                    {/* More Menu */}
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleCloseMore}
                        PaperProps={{
                            sx: {
                                borderRadius: '12px',
                                mt: 1,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                border: '1px solid rgba(0,0,0,0.05)'
                            }
                        }}
                    >
                        <MenuItem onClick={() => { onCopyToClipboard(translatedText); handleCloseMore(); }}>
                            <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="Copy Translation" />
                        </MenuItem>
                        <MenuItem onClick={() => { onAddGlossaryTerm(globalIndex); handleCloseMore(); }}>
                            <ListItemIcon><LibraryBooksIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="Add to Glossary" />
                        </MenuItem>
                    </Menu>
                </Grid>
            </Box>
        </Grid>
    );
};

export default TranslationRow;
